import { ProjectProgressSelector } from "@/features/projects/cards/ProjectProgressSelector";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type OpportunitySearch,
  type OpportunityStatusEnum,
  useMultiupdateOpportunity,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { getErrorMessage, hasItems } from "@repo/utils";
import { useState } from "react";

import { type EditModalMode } from "../utils";
import { MultiUpdateModal, createSaveSucess } from "./MultiUpdateModal";

interface ChangeStatusModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<EditModalMode>>;
  selectedOpportunities: OpportunitySearch[];
  setSelectedOpportunities: React.Dispatch<
    React.SetStateAction<OpportunitySearch[]>
  >;
  options: OpportunitiesSearchOptions;
  filters: OpportunitiesSearchFilters;
}
export const ChangeStatusModal = ({
  isOpen,
  setIsOpen,
  selectedOpportunities,
  setSelectedOpportunities,
  options,
  filters,
}: ChangeStatusModalProps) => {
  const { updateToast } = useToast();
  const { general } = useSettings();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedStatus, setSelectedStatus] = useState<
    OpportunityStatusEnum | undefined
  >(undefined);

  const [selectedReason, setSelectedReason] = useState<string | undefined>(
    undefined
  );
  const [description, setDescription] = useState<string | undefined>(undefined);
  const reasons = selectedStatus
    ? general?.reasons?.project_status_change?.[selectedStatus]
    : [];

  const updateProjects = useMultiupdateOpportunity(filters, options);

  const onSave = (selectedProjects: OpportunitySearch[]) => {
    setIsLoading(true);
    updateProjects(
      {
        status: selectedStatus,
        statusChangeDetails: {
          reason: selectedReason,
          description,
        },
      },
      selectedProjects.map((el) => el.opportunityId)
    )
      .then(
        createSaveSucess({
          setSelectedOpportunities,
          selectedOpportunities,
          selectedProjects,
          updateToast,
          setIsOpen,
        })
      )
      .catch((err) => {
        updateToast({
          title: t("prompt.project_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setDescription(undefined);
        setSelectedReason(undefined);
        setSelectedStatus(undefined);
      });
  };

  return (
    <MultiUpdateModal
      title={t("action.change_status")}
      isOpen={isOpen}
      isLoading={isLoading}
      setIsOpen={setIsOpen}
      selectedOpportunities={selectedOpportunities}
      onSave={
        (hasItems(reasons) && selectedReason) || !hasItems(reasons)
          ? onSave
          : undefined
      }
    >
      <Text textStyle="Heading.S">{t("label.status")}</Text>
      <ProjectProgressSelector
        setDescription={setDescription}
        setSelectedReason={setSelectedReason}
        setSelectedStatus={setSelectedStatus}
        selectedStatus={selectedStatus}
        currentStatus={selectedOpportunities?.[0]?.status}
        reasons={reasons}
      />
    </MultiUpdateModal>
  );
};
